import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiSearch,
  FiX,
  FiFolder,
  FiUsers,
  FiCopy,
  FiStar,
  FiAlertTriangle,
  FiSave,
  FiLayers,
  FiRefreshCw,
  FiBriefcase,
  FiMapPin
} from 'react-icons/fi';
import { faturamentoService, filterDataService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

import { BIMultiSelectSearch, BIPagination } from '../common';
import Loading from '../common/Loading';
const MultiSelectSearch = BIMultiSelectSearch;

const FilterCheckList = ({ label, items = [], selectedIds = [], onChange, badgeColor = '#6366f1' }) => {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    return items.filter(i => (i.name || '').toLowerCase().includes(search.toLowerCase()));
  }, [items, search]);

  const toggle = (id) => {
    const numId = Number(id);
    if (selectedIds.includes(numId)) {
      onChange(selectedIds.filter(i => i !== numId));
    } else {
      onChange([...selectedIds, numId]);
    }
  };

  const selectAll = () => {
    const allFilteredIds = filtered.map(i => Number(i.id));
    const newSelected = Array.from(new Set([...selectedIds, ...allFilteredIds]));
    onChange(newSelected);
  };

  const deselectAll = () => {
    const filteredIdsSet = new Set(filtered.map(i => Number(i.id)));
    onChange(selectedIds.filter(id => !filteredIdsSet.has(id)));
  };

  return (
    <div className="form-group-modern mb-3" style={{ width: '100%' }}>
      <div className="d-flex justify-content-between align-items-center mb-1">
        <label className="mb-0 fw-semibold text-dark" style={{ fontSize: '0.88rem' }}>
          {label} <span className="text-muted fw-normal" style={{ fontSize: '0.8rem' }}>({selectedIds.length} selecionados)</span>
        </label>
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none" style={{ fontSize: '0.78rem', color: badgeColor }} onClick={selectAll}>Marcar todos ({filtered.length})</button>
          <span className="text-muted">|</span>
          <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none text-secondary" style={{ fontSize: '0.78rem' }} onClick={deselectAll}>Desmarcar</button>
        </div>
      </div>
      
      <div style={{ position: 'relative', marginBottom: '8px', width: '100%' }}>
        <input
          type="text"
          className="form-control-modern form-control-sm"
          placeholder={`Buscar em ${label.toLowerCase()}...`}
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ paddingLeft: '32px', height: '34px', fontSize: '0.82rem', width: '100%' }}
        />
        <FiSearch size={14} color="#94a3b8" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }} />
      </div>

      <div style={{
        maxHeight: '260px', overflowY: 'auto', border: '1px solid var(--border, #e2e8f0)',
        borderRadius: '8px', padding: '8px', background: '#fafafa', width: '100%'
      }}>
        {filtered.length === 0 ? (
          <div className="text-muted text-center py-3 small">Nenhum item encontrado</div>
        ) : (
          filtered.map(item => {
            const isChecked = selectedIds.includes(Number(item.id));
            return (
              <label key={item.id} style={{
                display: 'flex', alignItems: 'center', gap: '10px', padding: '7px 12px',
                borderRadius: '6px', background: isChecked ? '#fff' : 'transparent',
                border: isChecked ? `1.5px solid ${badgeColor}` : '1px solid #f1f5f9',
                boxShadow: isChecked ? '0 1px 3px rgba(0,0,0,0.05)' : 'none',
                marginBottom: '4px', cursor: 'pointer', fontSize: '0.82rem', userSelect: 'none',
                width: '100%', transition: 'all 0.15s ease'
              }}>
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => toggle(item.id)}
                  style={{ width: '16px', height: '16px', cursor: 'pointer', flexShrink: 0 }}
                />
                <span style={{ fontWeight: isChecked ? 600 : 400, flex: 1, color: isChecked ? '#0f172a' : '#475569' }}>
                  {item.name}
                </span>
                {item.formula && (
                  <span className="badge bg-light text-secondary border small" style={{ fontSize: '0.7rem', padding: '3px 8px', fontFamily: 'monospace' }}>
                    {item.formula}
                  </span>
                )}
              </label>
            );
          })
        )}
      </div>
    </div>
  );
};

function GruposMetas() {
  const { hasSectionAccess } = useAuth();
  const canManage = hasSectionAccess('grupos_metas', 'manage');
  const [presets, setPresets] = useState([]);
  const [filters, setFilters] = useState([]);
  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterClassification, setFilterClassification] = useState('');
  const [filterSubtype, setFilterSubtype] = useState('');
  const [filterBranchSearch, setFilterBranchSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [message, setMessage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [totalRecords, setTotalRecords] = useState(0);

  const handlePerPageChange = (newPerPage) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [presetToDelete, setPresetToDelete] = useState(null);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [form, setForm] = useState({
    id: null,
    name: '',
    description: '',
    target_type: 'vendor',
    goal_classification: 'vendedor',
    goal_subtype: '',
    filter_branch: '',
    vendor_ids: '',
    employee_ids: '',
    filter_ids: [],
    variable_ids: [],
    is_default: false,
    active: true
  });

  const [sortField, setSortField] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) return <span style={{ marginLeft: 4, opacity: 0.25, fontSize: 10, userSelect: 'none' }}>▲▼</span>;
    return <span style={{ marginLeft: 4, fontSize: 10, userSelect: 'none' }}>{sortOrder === 'asc' ? '▲' : '▼'}</span>;
  };

  const loadPresets = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const res = await faturamentoService.listGoalPresets({
        page,
        per_page: perPage,
        search: searchTerm || undefined,
        target_type: filterType || undefined,
        goal_classification: filterClassification || undefined,
        goal_subtype: filterSubtype || undefined,
        branch: filterBranchSearch || undefined,
        sort_by: sortField,
        sort_order: sortOrder,
        ...params
      });
      setPresets(res?.data?.presets || []);
      setTotalRecords(res?.data?.total || 0);
    } catch (e) {
      setMessage({ type: 'danger', text: e.response?.data?.error || 'Erro ao listar grupos de metas' });
    } finally {
      setLoading(false);
    }
  }, [page, perPage, searchTerm, filterType, filterClassification, filterSubtype, filterBranchSearch, sortField, sortOrder]);

  const loadAuxData = useCallback(async () => {
    try {
      const [resFilters, resVars] = await Promise.all([
        faturamentoService.listSavedFilters({ active_only: 1 }),
        faturamentoService.listVariables({ per_page: 0 })
      ]);
      setFilters(resFilters?.data?.filters || []);
      setVariables(resVars?.data?.variables || []);
    } catch (e) {
      console.error('Erro ao carregar filtros e variáveis:', e);
    }
  }, []);

  useEffect(() => {
    loadAuxData();
  }, [loadAuxData]);

  useEffect(() => {
    loadPresets();
  }, [loadPresets]);

  const openNewModal = () => {
    setForm({
      id: null,
      name: '',
      description: '',
      target_type: 'vendor',
      goal_classification: 'vendedor',
      goal_subtype: '',
      filter_branch: '',
      vendor_ids: '',
      employee_ids: '',
      filter_ids: [],
      variable_ids: [],
      is_default: false,
      active: true
    });
    setShowModal(true);
  };

  const openEditModal = (p) => {
    setForm({
      id: p.id,
      name: p.name || '',
      description: p.description || '',
      target_type: p.target_type || 'vendor',
      goal_classification: p.goal_classification || 'vendedor',
      goal_subtype: p.goal_subtype || '',
      filter_branch: p.filter_branch || '',
      vendor_ids: p.vendor_ids || '',
      employee_ids: p.employee_ids || '',
      filter_ids: p.filter_ids_array || [],
      variable_ids: p.variable_ids_array || [],
      is_default: Boolean(p.is_default),
      active: Boolean(p.active)
    });
    setShowModal(true);
  };

  const replicatePreset = (p) => {
    setForm({
      id: null,
      name: `${p.name || ''} (Cópia)`,
      description: p.description || '',
      target_type: p.target_type || 'vendor',
      goal_classification: p.goal_classification || 'vendedor',
      goal_subtype: p.goal_subtype || '',
      filter_branch: p.filter_branch || '',
      vendor_ids: p.vendor_ids || '',
      employee_ids: p.employee_ids || '',
      filter_ids: p.filter_ids_array || [],
      variable_ids: p.variable_ids_array || [],
      is_default: false,
      active: true
    });
    setShowModal(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setErrorMessage('O nome do grupo de metas é obrigatório.');
      setShowErrorModal(true);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        target_type: form.target_type,
        goal_classification: form.goal_classification || 'vendedor',
        goal_subtype: form.goal_subtype || null,
        filter_branch: form.filter_branch || null,
        vendor_ids: form.vendor_ids || null,
        employee_ids: form.employee_ids || null,
        filter_ids: form.filter_ids.length > 0 ? form.filter_ids.join(',') : null,
        variable_ids: form.variable_ids.length > 0 ? form.variable_ids.join(',') : null,
        is_default: Boolean(form.is_default) ? 1 : 0,
        active: Boolean(form.active) ? 1 : 0
      };

      if (form.id) {
        await faturamentoService.updateGoalPreset(form.id, payload);
        setMessage({ type: 'success', text: 'Grupo de metas atualizado com sucesso!' });
      } else {
        await faturamentoService.createGoalPreset(payload);
        setMessage({ type: 'success', text: 'Grupo de metas cadastrado com sucesso!' });
      }

      setShowModal(false);
      loadPresets();
    } catch (e) {
      setErrorMessage(e.response?.data?.error || 'Erro ao salvar grupo de metas');
      setShowErrorModal(true);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!presetToDelete) return;
    setDeleting(true);
    try {
      await faturamentoService.deleteGoalPreset(presetToDelete.id);
      setMessage({ type: 'success', text: 'Grupo de metas excluído com sucesso!' });
      setShowDeleteModal(false);
      setPresetToDelete(null);
      loadPresets();
    } catch (e) {
      setMessage({ type: 'danger', text: e.response?.data?.error || 'Erro ao excluir grupo de metas' });
    } finally {
      setDeleting(false);
    }
  };

  const confirmBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setDeleting(true);
    try {
      for (const id of selectedIds) {
        await faturamentoService.deleteGoalPreset(id);
      }
      setMessage({ type: 'success', text: `${selectedIds.length} grupos de metas excluídos com sucesso!` });
      setSelectedIds([]);
      setShowBulkDeleteModal(false);
      loadPresets();
    } catch (e) {
      setMessage({ type: 'danger', text: e.response?.data?.error || 'Erro ao excluir grupos selecionados' });
    } finally {
      setDeleting(false);
    }
  };

  const toggleSelectAll = (items) => {
    if (selectedIds.length === items.length && items.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(items.map(i => i.id));
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const GOAL_CLASSIFICATIONS = [
    { value: 'vendedor', label: 'Vendedor', color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
    { value: 'bijou', label: 'Bijou', color: '#db2777', bg: '#fdf2f8', border: '#fbcfe8' },
    { value: 'operador_caixa', label: 'Operador de Caixa', color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
  ];

  const getGoalClassificationLabel = (classification) => {
    const found = GOAL_CLASSIFICATIONS.find(c => c.value === classification);
    return found ? found.label : (classification || 'Vendedor');
  };

  const getGoalClassificationBadge = (classification) => {
    const val = classification || 'vendedor';
    const item = GOAL_CLASSIFICATIONS.find(c => c.value === val) || GOAL_CLASSIFICATIONS[0];
    const label = getGoalClassificationLabel(val);
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: '12px',
        fontSize: '0.75rem', fontWeight: 600, backgroundColor: item.bg, color: item.color, border: `1px solid ${item.border}`,
        whiteSpace: 'nowrap'
      }}>
        {label}
      </span>
    );
  };

  const getGoalSubtypeLabel = (subtype) => {
    switch (subtype) {
      case 'clientes_positivados':
        return 'Clientes Positivados';
      case 'produtos_destaque':
        return 'Produtos Destaque';
      default:
        return 'Vendas (padrão)';
    }
  };

  const getGoalSubtypeBadge = (subtype) => {
    if (!subtype) {
      return (
        <span className="badge-pill-modern badge-empty-pill" style={{ fontSize: '0.72rem' }}>
          Vendas
        </span>
      );
    }
    if (subtype === 'clientes_positivados') {
      return (
        <span style={{
          display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: '12px',
          fontSize: '0.72rem', fontWeight: 600, backgroundColor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe',
          whiteSpace: 'nowrap'
        }}>
          Positivação
        </span>
      );
    }
    if (subtype === 'produtos_destaque') {
      return (
        <span style={{
          display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: '12px',
          fontSize: '0.72rem', fontWeight: 600, backgroundColor: '#fef3c7', color: '#b45309', border: '1px solid #fde68a',
          whiteSpace: 'nowrap'
        }}>
          Destaque
        </span>
      );
    }
    return (
      <span className="badge bg-light text-dark border" style={{ fontSize: '0.72rem' }}>
        {subtype}
      </span>
    );
  };

  const getTargetTypeBadge = (type, p) => {
    switch (type) {
      case 'vendor':
        return (
          <span
            className="badge-pill-modern badge-target-vendor"
            title={p?.vendor_ids_array?.length > 0 ? `${p.vendor_ids_array.length} vendedor(es) associado(s)` : 'Alvo: Vendedores'}
          >
            <FiUsers size={12} className="badge-ico" />
            <span>Vendedores</span>
            {p?.vendor_ids_array?.length > 0 && (
              <span className="badge-nested-counter badge-counter-vendor">
                {p.vendor_ids_array.length}
              </span>
            )}
          </span>
        );
      case 'employee':
        return (
          <span
            className="badge-pill-modern badge-target-employee"
            title={p?.default_employee_name ? `Funcionário: ${p.default_employee_name}` : (p?.employee_ids_array?.length > 0 ? `${p.employee_ids_array.length} funcionário(s) associado(s)` : 'Alvo: Funcionários')}
          >
            <FiBriefcase size={12} className="badge-ico" />
            <span>Funcionários</span>
            {p?.employee_ids_array?.length > 0 && (
              <span className="badge-nested-counter badge-counter-employee">
                {p.employee_ids_array.length}
              </span>
            )}
          </span>
        );
      case 'branch':
        return (
          <span className="badge-pill-modern badge-target-branch" title="Alvo: Filiais">
            <FiMapPin size={12} className="badge-ico" />
            <span>Filial</span>
          </span>
        );
      default:
        return (
          <span className="badge-pill-modern badge-target-general" title="Alvo: Geral">
            <FiLayers size={12} className="badge-ico" />
            <span>Geral</span>
          </span>
        );
    }
  };



  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <div>
          <h2 className="mb-1 fw-bold text-dark">Grupos de Metas</h2>
          <p className="text-muted mb-0">
            Configure grupos pré-definidos de filtros e variáveis para vendedores e funcionários
          </p>
        </div>
        {canManage && (
          <div className="d-flex gap-2">
            <button type="button" className="btn-modern btn-modern-primary" onClick={openNewModal}>
              <FiPlus size={16} className="me-2" />Novo Grupo de Metas
            </button>
          </div>
        )}
      </div>

      {message && <div className={`alert alert-${message.type} py-2 mb-4`} role="alert">{message.text}</div>}



      {/* Main Card */}
      <div className="settings-container">
        <div className="card-modern shadow-sm-modern" style={{ overflow: 'visible' }}>
          <div className="card-header-modern bg-transparent border-0 pt-4 px-4 pb-3 d-flex justify-content-between align-items-center flex-wrap gap-3" style={{ position: 'relative', zIndex: 100 }}>
            <div>
              <h5 className="mb-0 fw-bold text-dark">Grupos Cadastrados</h5>
            </div>
            
            <div className="d-flex align-items-center gap-3 flex-wrap grupos-filter-toolbar" style={{ position: 'relative', zIndex: 100 }}>
              <div className="grupos-filter-item grupos-filter-search" style={{ position: 'relative', width: '250px' }}>
                <FiSearch
                  size={16}
                  className="bi-search-input-icon"
                  style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#64748b', zIndex: 1, pointerEvents: 'none' }}
                />
                <input
                  type="text"
                  className="form-control-modern"
                  placeholder="Buscar grupo..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ paddingLeft: '38px', height: '40px' }}
                />
              </div>

              <select
                className="form-control-modern grupos-filter-item grupos-filter-type"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                style={{ width: '160px', height: '40px' }}
                title="Filtrar por tipo"
              >
                <option value="">Todos os tipos</option>
                <option value="vendor">Vendedores</option>
                <option value="employee">Funcionários</option>
                <option value="branch">Filiais</option>
                <option value="general">Gerais</option>
              </select>

              <select
                className="form-control-modern grupos-filter-item grupos-filter-classification"
                value={filterClassification}
                onChange={(e) => setFilterClassification(e.target.value)}
                style={{ width: '170px', height: '40px' }}
                title="Filtrar por classificação"
              >
                <option value="">Todas classificações</option>
                <option value="vendedor">Vendedor</option>
                <option value="bijou">Bijou</option>
                <option value="operador_caixa">Operador de Caixa</option>
              </select>

              <select
                className="form-control-modern grupos-filter-item grupos-filter-subtype"
                value={filterSubtype}
                onChange={(e) => setFilterSubtype(e.target.value)}
                style={{ width: '170px', height: '40px' }}
                title="Filtrar por subtipo"
              >
                <option value="">Todos subtipos</option>
                <option value="vendas">Vendas (padrão)</option>
                <option value="clientes_positivados">Clientes Positivados</option>
                <option value="produtos_destaque">Produtos Destaque</option>
              </select>

              <div className="grupos-filter-item grupos-filter-branch" style={{ width: '200px', position: 'relative', zIndex: 110 }}>
                <MultiSelectSearch
                  label=""
                  placeholder="Filtrar filial..."
                  value={filterBranchSearch}
                  onChange={(val) => setFilterBranchSearch(val)}
                  fetchData={filterDataService.getFiliais}
                  badgeColor="#10b981"
                  showCodePrefix
                />
              </div>

              <div className="grupos-filter-actions d-flex align-items-center gap-2 flex-wrap">
                {(searchTerm || filterType || filterClassification || filterSubtype || filterBranchSearch) && (
                  <button
                    type="button"
                    className="btn-modern btn-modern-light btn-modern-sm"
                    onClick={() => { setSearchTerm(''); setFilterType(''); setFilterClassification(''); setFilterSubtype(''); setFilterBranchSearch(''); }}
                    title="Limpar filtros"
                  >
                    <FiX size={14} />
                  </button>
                )}

                <button
                  type="button"
                  className="btn-modern btn-modern-light btn-modern-sm"
                  onClick={() => loadPresets()}
                  title="Atualizar lista"
                >
                  <FiRefreshCw size={14} className={loading ? 'spin-ico' : ''} />
                </button>

                {canManage && selectedIds.length > 0 && (
                  <button
                    type="button"
                    className="btn-modern btn-modern-light-danger btn-modern-sm animate__animated animate__fadeIn"
                    onClick={() => setShowBulkDeleteModal(true)}
                  >
                    <FiTrash2 size={14} className="me-1" />
                    Excluir ({selectedIds.length})
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="card-body-modern p-4 pt-0" style={{ position: 'relative', zIndex: 1 }}>
            {loading && presets.length > 0 && (
              <Loading.Overlay text="Atualizando grupos de metas..." size="sm" />
            )}
            <div className="table-responsive">
              <table className="table-modern w-100" style={{ minWidth: '700px' }}>
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>
                      <input
                        type="checkbox"
                        checked={presets.length > 0 && selectedIds.length === presets.length}
                        onChange={() => toggleSelectAll(presets)}
                      />
                    </th>
                    <th className="sortable-header" style={{ width: '65px' }} onClick={() => handleSort('id')}>
                      ID{renderSortIcon('id')}
                    </th>
                    <th className="sortable-header" onClick={() => handleSort('name')}>
                      Nome do Grupo{renderSortIcon('name')}
                    </th>
                    <th className="sortable-header" onClick={() => handleSort('target_type')}>
                      Alvo{renderSortIcon('target_type')}
                    </th>
                    <th className="sortable-header" onClick={() => handleSort('goal_classification')}>
                      Classificação{renderSortIcon('goal_classification')}
                    </th>
                    <th className="sortable-header" onClick={() => handleSort('goal_subtype')}>
                      Subtipo{renderSortIcon('goal_subtype')}
                    </th>
                    <th className="sortable-header" onClick={() => handleSort('filter_branch')}>
                      Filiais{renderSortIcon('filter_branch')}
                    </th>
                    <th className="sortable-header" onClick={() => handleSort('is_default')}>
                      Padrão{renderSortIcon('is_default')}
                    </th>
                    <th className="sortable-header" onClick={() => handleSort('active')}>
                      Status{renderSortIcon('active')}
                    </th>
                    <th className="text-end">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && presets.length === 0 ? (
                    <Loading.Table
                      colSpan={10}
                      text="Carregando grupos de metas..."
                      subtext="Buscando presets, tipos de alvos, filiais e configurações associadas..."
                    />
                  ) : presets.length === 0 ? (
                    <tr><td colSpan="10" className="text-center py-5 text-muted">Nenhum grupo de metas cadastrado.</td></tr>
                  ) : (
                    presets.map(p => {
                      return (
                        <tr key={p.id} className="preset-table-row">
                          <td className="align-middle">
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(p.id)}
                              onChange={() => toggleSelectOne(p.id)}
                            />
                          </td>
                          <td className="align-middle">
                            <span className="badge-id-code">#{p.id}</span>
                          </td>
                          <td className="align-middle">
                            <div className="fw-semibold text-dark" style={{ fontSize: '0.92rem' }}>{p.name}</div>
                            {p.description && <div className="text-muted small mt-1" style={{ fontSize: '0.78rem', lineHeight: '1.3' }}>{p.description}</div>}
                          </td>
                          <td className="align-middle">
                            {getTargetTypeBadge(p.target_type, p)}
                          </td>
                          <td className="align-middle">
                            {getGoalClassificationBadge(p.goal_classification)}
                          </td>
                          <td className="align-middle">
                            {getGoalSubtypeBadge(p.goal_subtype)}
                          </td>
                          <td className="align-middle">
                            {p.filter_branch ? (
                              <div className="d-flex flex-wrap gap-1" style={{ maxWidth: '170px' }}>
                                {p.filter_branch.split(',').filter(Boolean).map((br, idx) => (
                                  <span key={idx} className="badge-branch-pill">
                                    <span className="badge-dot-emerald" />
                                    {br.trim()}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="badge-pill-modern badge-empty-pill">
                                Todas as Filiais
                              </span>
                            )}
                          </td>
                          <td className="align-middle">
                            {p.is_default ? (
                              <span className="badge-pill-modern badge-default-star">
                                <FiStar size={12} className="badge-star-ico" />
                                <span>Padrão</span>
                              </span>
                            ) : (
                              <span className="badge-pill-modern badge-empty-pill">
                                Opcional
                              </span>
                            )}
                          </td>
                          <td className="align-middle">
                            {p.active ? (
                              <span className="badge-pill-modern badge-status-active">
                                <span className="badge-pulse-dot" />
                                Ativo
                              </span>
                            ) : (
                              <span className="badge-pill-modern badge-status-inactive">
                                <span className="badge-dot-gray" />
                                Inativo
                              </span>
                            )}
                          </td>
                          <td className="align-middle text-end">
                            <div className="d-flex justify-content-end align-items-center gap-1">
                              {canManage ? (
                                <>
                                  <button
                                    type="button"
                                    className="btn-modern btn-modern-light btn-modern-sm"
                                    onClick={() => replicatePreset(p)}
                                    title="Replicar grupo"
                                  >
                                    <FiCopy size={14} />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn-modern btn-modern-light btn-modern-sm"
                                    onClick={() => openEditModal(p)}
                                    title="Editar grupo"
                                  >
                                    <FiEdit2 size={14} />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn-modern btn-modern-light-danger btn-modern-sm"
                                    onClick={() => { setPresetToDelete(p); setShowDeleteModal(true); }}
                                    title="Excluir grupo"
                                  >
                                    <FiTrash2 size={14} />
                                  </button>
                                </>
                              ) : (
                                <button
                                  type="button"
                                  className="btn-modern btn-modern-light btn-modern-sm"
                                  onClick={() => openEditModal(p)}
                                  title="Visualizar grupo"
                                >
                                  <FiSearch size={14} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            <BIPagination
              page={page}
              perPage={perPage}
              total={totalRecords}
              onPageChange={(p) => setPage(p)}
              onPerPageChange={handlePerPageChange}
              itemLabel="registros"
              perPageOptions={[10, 25, 50, 100, 200]}
            />
          </div>
        </div>
      </div>

      {/* Modal Cadastro / Edição */}
      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, backdropFilter: 'blur(4px)', padding: '20px'
        }}>
          <div style={{
            background: 'white', borderRadius: '16px', width: '100%', maxWidth: '1120px',
            maxHeight: '90vh', display: 'flex', flexDirection: 'column',
            boxShadow: '0 24px 60px rgba(0,0,0,0.25)', overflow: 'hidden'
          }}>
            <div style={{
              padding: '20px 28px', borderBottom: '1px solid var(--border)',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <div className="d-flex align-items-center gap-3">
                <div style={{ width: 40, height: 40, borderRadius: 10, background: '#e0e7ff', color: '#4338ca', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FiFolder size={20} />
                </div>
                <div>
                  <h5 style={{ margin: 0, fontWeight: 700, fontSize: '1.05rem' }}>
                    {form.id ? 'Editar Grupo de Metas' : 'Novo Grupo de Metas'}
                  </h5>
                  <p style={{ margin: 0, fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                    Defina os filtros e variáveis padrão aplicáveis a metas
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
              >
                <FiX size={20} color="#64748b" />
              </button>
            </div>

            <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              <div style={{ padding: '24px 28px', overflowY: 'auto', flex: 1 }}>
                <div className="row g-3 mb-3">
                  <div className="col-md-12">
                    <div className="form-group-modern mb-0">
                      <label>Nome do Grupo <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control-modern"
                        placeholder="Ex: Padrão Vendedores - São Félix"
                        required
                        value={form.name}
                        onChange={e => setForm({ ...form, name: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-md-4">
                    <div className="form-group-modern mb-0">
                      <label>Tipo de Alvo</label>
                      <select
                        className="form-select-modern"
                        value={form.target_type}
                        onChange={e => setForm({ ...form, target_type: e.target.value })}
                      >
                        <option value="vendor">Vendedores</option>
                        <option value="employee">Funcionários</option>
                        <option value="branch">Filiais</option>
                        <option value="general">Geral</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group-modern mb-0">
                      <label>Classificação da Meta</label>
                      <select
                        className="form-select-modern"
                        value={form.goal_classification || 'vendedor'}
                        onChange={e => setForm({ ...form, goal_classification: e.target.value })}
                      >
                        <option value="vendedor">Vendedor</option>
                        <option value="bijou">Bijou</option>
                        <option value="operador_caixa">Operador de Caixa</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group-modern mb-0">
                      <label>Subtipo</label>
                      <select
                        className="form-select-modern"
                        value={form.goal_subtype || ''}
                        onChange={e => setForm({ ...form, goal_subtype: e.target.value })}
                      >
                        <option value="">Vendas (padrão)</option>
                        <option value="clientes_positivados">Clientes Positivados</option>
                        <option value="produtos_destaque">Produtos Destaque</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="form-group-modern mb-3">
                  <label>Descrição</label>
                  <textarea
                    className="form-control-modern"
                    rows="2"
                    placeholder="Descrição opcional das regras ou contexto deste grupo de metas..."
                    value={form.description}
                    onChange={e => setForm({ ...form, description: e.target.value })}
                    style={{ resize: 'none' }}
                  />
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-md-12">
                    <MultiSelectSearch
                      label="Filiais Aplicáveis (opcional, vazio = todas)"
                      placeholder="Selecionar filiais..."
                      value={form.filter_branch}
                      onChange={val => setForm({ ...form, filter_branch: val })}
                      fetchData={filterDataService.getFiliais}
                      badgeColor="#10b981"
                      showCodePrefix
                    />
                  </div>
                </div>

                {form.target_type === 'vendor' && (
                  <div className="mb-3">
                    <MultiSelectSearch
                      label="Vendedores Vinculados (RCA)"
                      placeholder="Buscar vendedores para vincular a este grupo..."
                      value={form.vendor_ids}
                      onChange={val => setForm({ ...form, vendor_ids: val })}
                      fetchData={filterDataService.getVendedores}
                      badgeColor="#3b82f6"
                      showCodePrefix
                    />
                  </div>
                )}

                {form.target_type === 'employee' && (
                  <div className="mb-3">
                    <MultiSelectSearch
                      label="Funcionários Vinculados (Winthor)"
                      placeholder="Buscar funcionários para vincular a este grupo..."
                      value={form.employee_ids}
                      onChange={val => setForm({ ...form, employee_ids: val })}
                      fetchData={(search, limit) => faturamentoService.listEmployees({ search, per_page: limit || 100 })}
                      badgeColor="#f59e0b"
                      showCodePrefix
                    />
                  </div>
                )}

                <div className="mb-4" style={{ width: '100%' }}>
                  <FilterCheckList
                    label="Filtros Vinculados"
                    items={filters}
                    selectedIds={form.filter_ids}
                    onChange={ids => setForm({ ...form, filter_ids: ids })}
                    badgeColor="#3b82f6"
                  />
                </div>

                <div className="mb-4" style={{ width: '100%' }}>
                  <FilterCheckList
                    label="Variáveis Vinculadas"
                    items={variables}
                    selectedIds={form.variable_ids}
                    onChange={ids => setForm({ ...form, variable_ids: ids })}
                    badgeColor="#8b5cf6"
                  />
                </div>

                <div className="row g-3 mb-2">
                  <div className="col-md-6">
                    <div className="form-check-modern" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        id="preset_is_default"
                        className="form-check-input"
                        checked={form.is_default}
                        onChange={e => setForm({ ...form, is_default: e.target.checked })}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <label className="form-check-label" htmlFor="preset_is_default" style={{ cursor: 'pointer', fontSize: '0.85rem' }}>
                        <strong>Grupo Padrão Principal</strong> (usado como fallback automático)
                      </label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-check-modern" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        id="preset_active"
                        className="form-check-input"
                        checked={form.active}
                        onChange={e => setForm({ ...form, active: e.target.checked })}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <label className="form-check-label" htmlFor="preset_active" style={{ cursor: 'pointer', fontSize: '0.85rem' }}>
                        Ativo
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div style={{
                padding: '16px 28px 24px', borderTop: '1px solid var(--border)',
                display: 'flex', justifyContent: 'flex-end', gap: '12px', background: 'white'
              }}>
                <button
                  type="button"
                  className="btn-modern btn-modern-secondary"
                  onClick={() => setShowModal(false)}
                  disabled={saving}
                >
                  {canManage ? 'Cancelar' : 'Fechar'}
                </button>
                {canManage && (
                  <button
                    type="submit"
                    className="btn-modern btn-modern-primary"
                    disabled={saving}
                  >
                    {saving ? 'Salvando...' : <><FiSave size={16} className="me-1" />{form.id ? 'Atualizar Grupo' : 'Salvar Grupo'}</>}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Confirm Delete */}
      {showDeleteModal && presetToDelete && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, backdropFilter: 'blur(4px)', padding: '20px'
        }}>
          <div style={{
            background: 'white', borderRadius: '16px', width: '100%', maxWidth: '420px',
            boxShadow: '0 24px 60px rgba(0,0,0,0.25)', overflow: 'hidden'
          }}>
            <div style={{ padding: '32px 28px 20px', textAlign: 'center' }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#fee2e2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <FiAlertTriangle size={28} />
              </div>
              <h4 style={{ margin: '0 0 8px', fontWeight: 700, fontSize: '1.2rem', color: '#1e293b' }}>
                Excluir Grupo de Metas
              </h4>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748b' }}>
                Tem certeza que deseja excluir o grupo <strong>{presetToDelete.name}</strong>?
              </p>
            </div>
            <div style={{ padding: '16px 28px 24px', display: 'flex', justifyContent: 'center', gap: '12px' }}>
              <button
                type="button"
                className="btn-modern btn-modern-secondary"
                onClick={() => { setShowDeleteModal(false); setPresetToDelete(null); }}
                disabled={deleting}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-modern btn-modern-danger"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? 'Excluindo...' : 'Sim, Excluir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Bulk Delete */}
      {showBulkDeleteModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, backdropFilter: 'blur(4px)', padding: '20px'
        }}>
          <div style={{
            background: 'white', borderRadius: '16px', width: '100%', maxWidth: '420px',
            boxShadow: '0 24px 60px rgba(0,0,0,0.25)', overflow: 'hidden'
          }}>
            <div style={{ padding: '32px 28px 20px', textAlign: 'center' }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#fee2e2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <FiTrash2 size={28} />
              </div>
              <h4 style={{ margin: '0 0 8px', fontWeight: 700, fontSize: '1.2rem', color: '#1e293b' }}>
                Excluir Grupos Selecionados
              </h4>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748b' }}>
                Deseja excluir permanentemente <strong>{selectedIds.length}</strong> grupos de metas?
              </p>
            </div>
            <div style={{ padding: '16px 28px 24px', display: 'flex', justifyContent: 'center', gap: '12px' }}>
              <button
                type="button"
                className="btn-modern btn-modern-secondary"
                onClick={() => setShowBulkDeleteModal(false)}
                disabled={deleting}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-modern btn-modern-danger"
                onClick={confirmBulkDelete}
                disabled={deleting}
              >
                {deleting ? 'Excluindo...' : 'Sim, Excluir Todos'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Error */}
      {showErrorModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10000, backdropFilter: 'blur(4px)', padding: '20px'
        }}>
          <div style={{
            background: 'white', borderRadius: '16px', width: '100%', maxWidth: '400px',
            boxShadow: '0 24px 60px rgba(0,0,0,0.25)', overflow: 'hidden', textAlign: 'center', padding: '28px'
          }}>
            <div style={{ width: 50, height: 50, borderRadius: '50%', background: '#fee2e2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <FiAlertTriangle size={24} />
            </div>
            <h5 className="fw-bold mb-2 text-dark">Atenção</h5>
            <p className="text-muted small mb-4">{errorMessage}</p>
            <button
              type="button"
              className="btn-modern btn-modern-primary w-100"
              onClick={() => setShowErrorModal(false)}
            >
              Entendido
            </button>
          </div>
        </div>
      )}

      <style>{`
        .spin-ico { animation: spin-ico 0.8s linear infinite; display: inline-block; }
        @keyframes spin-ico { to { transform: rotate(360deg); } }

        .preset-table-row {
          transition: background-color 0.15s ease-in-out;
        }
        .preset-table-row:hover {
          background-color: #f8fafc;
        }

        .sortable-header {
          cursor: pointer;
          user-select: none;
          transition: background-color 0.15s ease, color 0.15s ease;
        }
        .sortable-header:hover {
          background-color: #f1f5f9;
          color: #4338ca;
        }

        .badge-pill-modern {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 0.76rem;
          font-weight: 600;
          letter-spacing: 0.01em;
          white-space: nowrap;
          transition: all 0.15s ease-in-out;
          user-select: none;
        }
        .badge-pill-modern:hover {
          transform: translateY(-1px);
        }

        .badge-ico {
          flex-shrink: 0;
        }

        /* Target types */
        .badge-target-vendor {
          background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%);
          color: #3730a3;
          border: 1px solid #c7d2fe;
          box-shadow: 0 1px 2px rgba(99, 102, 241, 0.08);
        }
        .badge-target-employee {
          background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
          color: #92400e;
          border: 1px solid #fde68a;
          box-shadow: 0 1px 2px rgba(245, 158, 11, 0.08);
        }
        .badge-target-branch {
          background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
          color: #166534;
          border: 1px solid #bbf7d0;
          box-shadow: 0 1px 2px rgba(22, 101, 52, 0.08);
        }
        .badge-target-general {
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          color: #334155;
          border: 1px solid #e2e8f0;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
        }

        /* Nested counters inside type badges */
        .badge-nested-counter {
          border-radius: 10px;
          padding: 0 6px;
          font-size: 0.68rem;
          font-weight: 700;
          color: #ffffff;
          line-height: 1.4;
          margin-left: 2px;
        }
        .badge-counter-vendor {
          background: #4f46e5;
        }
        .badge-counter-employee {
          background: #d97706;
        }

        /* Branch chip badges */
        .badge-branch-pill {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 2px 7px;
          border-radius: 8px;
          font-size: 0.72rem;
          font-weight: 600;
          background: #f0fdf4;
          color: #15803d;
          border: 1px solid #bbf7d0;
          box-shadow: 0 1px 2px rgba(22, 101, 52, 0.04);
          transition: transform 0.12s ease;
        }
        .badge-branch-pill:hover {
          transform: translateY(-1px);
        }
        .badge-dot-emerald {
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background-color: #22c55e;
          flex-shrink: 0;
        }

        /* Neutral / empty pill */
        .badge-empty-pill {
          background: #f8fafc;
          color: #94a3b8;
          border: 1px solid #e2e8f0;
          font-weight: 500;
          font-size: 0.72rem;
          padding: 3px 8px;
        }

        /* Filters & Variables badges */
        .badge-filter-count {
          background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
          color: #1d4ed8;
          border: 1px solid #bfdbfe;
          box-shadow: 0 1px 2px rgba(37, 99, 235, 0.08);
          cursor: help;
        }
        .badge-variable-count {
          background: linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%);
          color: #7e22ce;
          border: 1px solid #e9d5ff;
          box-shadow: 0 1px 2px rgba(126, 34, 206, 0.08);
          cursor: help;
        }
        .badge-preview-subtext {
          font-size: 0.72rem;
          color: #64748b;
          max-width: 260px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          padding-left: 2px;
        }

        /* Default star badge */
        .badge-default-star {
          background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
          color: #92400e;
          border: 1px solid #fcd34d;
          box-shadow: 0 2px 4px rgba(217, 119, 6, 0.15);
          letter-spacing: 0.02em;
        }
        .badge-star-ico {
          fill: #d97706;
          color: #d97706;
          flex-shrink: 0;
        }

        /* Status badges */
        .badge-status-active {
          background: #f0fdf4;
          color: #15803d;
          border: 1px solid #bbf7d0;
          box-shadow: 0 1px 2px rgba(22, 101, 52, 0.06);
        }
        .badge-status-inactive {
          background: #f8fafc;
          color: #64748b;
          border: 1px solid #cbd5e1;
        }
        .badge-pulse-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background-color: #22c55e;
          box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.25);
          display: inline-block;
          flex-shrink: 0;
        }
        .badge-dot-gray {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background-color: #94a3b8;
          display: inline-block;
          flex-shrink: 0;
        }

        /* ID code badge */
        .badge-id-code {
          font-family: SFMono-Regular, Menlo, Monaco, Consolas, monospace;
          font-size: 0.74rem;
          font-weight: 600;
          color: #64748b;
          background-color: #f8fafc;
          border: 1px solid #e2e8f0;
          padding: 2px 7px;
          border-radius: 6px;
          display: inline-block;
        }
      `}</style>
    </div>
  );
}

export default GruposMetas;
